/**
 * ForegroundNotificationHandler
 *
 * 포그라운드 상태에서 수신된 FCM 메시지를 처리하고
 * Toast 알림을 표시하는 헬퍼 클래스입니다.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import ToastNotification from '../components/atoms/ToastNotification';

class ForegroundNotificationHandler {
  constructor() {
    this.toastContainer = null;
    this.activeToasts = [];
    this.maxToasts = 3; // 최대 동시 표시 Toast 개수
    this.toastIdCounter = 0;
  }

  /**
   * Toast 컨테이너 초기화
   *
   * DOM에 Toast를 렌더링할 컨테이너를 생성합니다.
   */
  initializeToastContainer() {
    if (!this.toastContainer) {
      this.toastContainer = document.createElement('div');
      this.toastContainer.id = 'toast-notification-container';
      this.toastContainer.style.position = 'fixed';
      this.toastContainer.style.zIndex = '9999';
      this.toastContainer.style.pointerEvents = 'none';
      document.body.appendChild(this.toastContainer);
      console.log('[ForegroundNotificationHandler] Toast 컨테이너 초기화 완료');
    }
  }

  /**
   * FCM 메시지 처리 및 Toast 표시
   */
  handleForegroundMessage(payload) {
    try {
      console.log('알림! payload = ', payload);

      const notification = payload.notification || {};
      const data = payload.data || {};

      const duration = 5000;
      const icon = notification.icon || data.icon || '/images/checkmark-256.png';

      this.initializeToastContainer();

      if (this.activeToasts.length >= this.maxToasts) {
        this.removeOldestToast();
      }

      const toastId = `toast-${this.toastIdCounter}`;
      this.toastIdCounter += 1;

      const toastWrapper = document.createElement('div');
      toastWrapper.id = toastId;
      this.toastContainer.appendChild(toastWrapper);

      const handleToastClick = (toastData) => {
        ForegroundNotificationHandler.handleNotificationRouting(toastData);
      };

      const handleToastClose = () => this.removeToast(toastId);

      ReactDOM.render(
        <ToastNotification
          title={data.title || '새 알림'}
          body={data.body || ''}
          icon={icon}
          data={data}
          onClick={handleToastClick}
          onClose={handleToastClose}
          duration={duration}
        />,
        toastWrapper,
      );

      this.activeToasts.push({ id: toastId, wrapper: toastWrapper, timestamp: Date.now() });
    } catch (error) {
      console.error('메시지 처리 오류:', error);
    }
  }

  /**
   * Toast 제거
   */
  removeToast(toastId) {
    const toastIndex = this.activeToasts.findIndex((t) => t.id === toastId);
    if (toastIndex !== -1) {
      const toast = this.activeToasts[toastIndex];
      if (toast.wrapper && toast.wrapper.parentNode) {
        ReactDOM.unmountComponentAtNode(toast.wrapper);
        toast.wrapper.parentNode.removeChild(toast.wrapper);
      }
      this.activeToasts.splice(toastIndex, 1);
    }
  }

  removeOldestToast() {
    if (this.activeToasts.length > 0) {
      this.removeToast(this.activeToasts[0].id);
    }
  }

  removeAllToasts() {
    [...this.activeToasts].forEach((toast) => this.removeToast(toast.id));
  }

  /**
   * 알림 타입에 따른 라우팅
   */
  static handleNotificationRouting(data) {
    if (!data) return;

    const { type, id, url } = data;

    if (url) {
      window.location.href = url;
      return;
    }

    const routes = {
      message: `/chat/${id}`,
      task: `/tasks/${id}`,
      announcement: `/announcements/${id}`,
      comment: `/posts/${id}`,
      follow: `/profile/${id}`,
    };

    window.location.href = data.link || routes[type] || '/';
  }

  cleanup() {
    this.removeAllToasts();
    if (this.toastContainer && this.toastContainer.parentNode) {
      this.toastContainer.parentNode.removeChild(this.toastContainer);
      this.toastContainer = null;
    }
  }
}

// 싱글톤 인스턴스 생성
const foregroundNotificationHandler = new ForegroundNotificationHandler();

export default foregroundNotificationHandler;
